import {WorkerResponse} from "@/utilities/workerUtilites";
import {useAppStore} from "@/stores/app";
import {storeToRefs} from "pinia";
import {useUserStore} from "../stores/user.js";

const ENDPOINTS = {
  API: 'api',
  V1: 'v1',
  USERS: 'users',
  GROUPS: 'groups',
  CUSTOMERS: 'customers',
  SIGNIN: 'signin',
  RESET: 'reset',
  AUTH: 'auth',
  DOM: 'doms',
  STATUSES: 'statuses',
  PROGRESSIONS: 'progressions',
  LABELS: 'labels',
  ORDERS: 'commissions',
  MEDIA: 'media',
  SUBSTATUSES: 'substatuses',
  PROVINCES: 'provinces',
  NOTIFICATIONS: 'notifications',
  TOKEN: 'token',
  EVENTS: 'events',
  CONFIG: 'config',
  GLOBALS: 'globals',
  CATEGORIES: 'categories'
};

const QS_PARAMS = {
  LABEL_PREFIX: 'prefix='
};

export const QUERY_FIELDS = {
  order_by: 'orderBy',
  asc: 'asc',
  page: 'page',
  page_size: 'page_size',
  search: 'search',
  created_by: 'created_by',
  completed: 'completed',
  seller: 'created_by'
};

let worker;
const WorkerMessageObserver = {};

export class EndpointsFactory {
  static ENDPOINTS = {
    LOGIN: 'login_endpoint',
    RESET_PWD: 'reset_endpoint',
    LOGOUT: 'logout_endpoint',

    USERS_GET: 'users_get_endpoint',
    USER_DELETE: 'user_delete_endpoint',
    USER_GET: 'user_get_endpoint',
    USER_PATCH: 'user_patch_endpoint',
    USER_POST: 'user_post_endpoint',
    USER_PWD_RESET_POST: 'user_pwd_reset_post_endpoint',
    USER_PATCH_PUSH_TOKEN: 'user_patch_push_token',

    GROUPS_GET: 'groups_get_endpoint',
    GROUP_POST: 'group_post_endpoint',
    GROUP_DELETE: 'group_delete_endpoint',
    GROUP_GET: 'group_get_endpoint',
    GROUP_PATCH: 'group_patch_endpoint',

    CUSTOMERS_GET: 'customers_get_endpoint',
    CUSTOMER_DELETE: 'customer_delete_endpoint',
    CUSTOMER_GET: 'customer_get_endpoint',
    CUSTOMER_PATCH: 'customer_patch_endpoint',
    CUSTOMER_POST: 'customer_post_endpoint',

    ORDERS_GET: 'orders_get_endpoint',
    ORDER_DELETE: 'order_delete_endpoint',
    ORDER_GET: 'order_get_endpoint',
    ORDER_PATCH: 'order_patch_endpoint',
    ORDER_POST: 'order_post_endpoint',

    UPLOAD_TYPES_GET: 'upload_types_get_endpoint',
    UPLOAD_TYPE_POST: 'upload_type_post_endpoint',
    UPLOAD_TYPE_GET: 'upload_type_get_endpoint',
    UPLOAD_TYPE_PATCH: 'upload_type_patch_endpoint',

    ORDER_TYPES_GET: 'order_types_get_endpoint',
    ORDER_TYPE_POST: 'order_type_post_endpoint',
    ORDER_TYPE_GET: 'order_type_get_endpoint',
    ORDER_TYPE_PATCH: 'order_type_patch_endpoint',

    DOM_GET_STATUSES: 'dom_get_statuses_endpoint',
    DOM_GET_PROGRESSIONS: 'dom_get_progressions_endpoint',
    DOM_GET_PROGRESSION_SUBSTATUSES: 'dom_get_progression_substatuses_endpoint',
    DOM_GET_PROVINCES: 'dom_get_provinces',
    DOM_GET_NOTIFICATIONS: 'dom_get_notifications',

    LABELS_GET: 'labels_get_endpoint',

    UPLOAD_MEDIA: 'upload_media_endpoint',
    GET_MEDIA: 'get_media_endpoint',
    DELETE_MEDIA: 'delete_media_endpoint',

    GLOBALS_GET: 'globals_get_endpoint',

    ORDER_STATS_GET: 'order_stats_get_endpoint'
  };

  static #API_endpoint = import.meta.env.VITE_API_ENDPOINT + "/" + ENDPOINTS.API + "/" + ENDPOINTS.V1;

  static #auth_endpoint = this.#API_endpoint + "/" + ENDPOINTS.USERS;
  static #users_endpoint = this.#API_endpoint + "/" + ENDPOINTS.USERS;
  static #dom_endpoint = this.#API_endpoint + "/" + ENDPOINTS.DOM;
  static #labels_endpoint = this.#API_endpoint + "/" + ENDPOINTS.LABELS;
  static #groups_endpoint = this.#API_endpoint + "/" + ENDPOINTS.GROUPS;
  static #customers_endpoint = this.#API_endpoint + "/" + ENDPOINTS.CUSTOMERS;
  static #orders_endpoint = this.#API_endpoint + "/" + ENDPOINTS.ORDERS;
  static #media_endpoint = this.#API_endpoint + "/" + ENDPOINTS.MEDIA;
  static #config_endpoint = this.#API_endpoint + "/" + ENDPOINTS.CONFIG;

  static initWorker(newWorker){
    worker = newWorker;
    worker.addEventListener('message', handleWorkerMessage);
  }

  //answerCode identifica la risposta alla chiamata api. Così l'observer sa cosa aspettare e non risponde a una chiamata a caso.
  static execApiViaWorker({
                            url,
                            token,
                            contentType,
                            method,
                            body,
                            formData,
                            decodeResponseType
                          }, answerCode, successCallback, failCallback, avoidAutoRefresh = false){
    if(!worker){
      throw 'WebWorker not initialised';
    }
    if(answerCode){
      if(!WorkerMessageObserver[answerCode]){
        WorkerMessageObserver[answerCode] = [];
      }
      WorkerMessageObserver[answerCode].push({
        onSuccess: successCallback,
        onFail: failCallback
      });
    }
    worker.postMessage({
      url: url,
      token: token,
      contentType: contentType,
      method: method,
      responseCode: answerCode,
      body: body,
      formData: formData,
      decodeResponseType: decodeResponseType,
      avoidAutoRefresh: avoidAutoRefresh
    });
  }

  static fetchGlobalHeaders(token = null, contentType = 'application/json'){
    const headers = new Headers();
    if(contentType){
      headers.append('Content-Type', contentType);
    }
    headers.append('Accept', 'application/json');
    if(token){
      headers.append('Authorization', `Bearer ${token}`)
    }
    return headers;
  }

  static getEndpoint(endpoint_name, {
    query_parameters,
    user_id,
    group_id,
    customer_id,
    order_id,
    media_id,
    upload_type_id,
    order_type_id,
    language_code,
    labels_prefix
  } = {}){
    let url;
    switch (endpoint_name){
      case this.ENDPOINTS.LOGIN:
        return this.#auth_endpoint + "/" + ENDPOINTS.AUTH;
      case this.ENDPOINTS.LOGOUT:
        if(!user_id){
          throw 'User id is required';
        }
        return this.#auth_endpoint + "/" + ENDPOINTS.SIGNIN + "/" + user_id;

      case this.ENDPOINTS.USERS_GET:
        url = this.#users_endpoint;
        return appendQueryParams(url, query_parameters);

      case this.ENDPOINTS.USER_POST:
        return this.#users_endpoint;
      case this.ENDPOINTS.USER_DELETE:
      case this.ENDPOINTS.USER_GET:
      case this.ENDPOINTS.USER_PATCH:
        if(!user_id){
          throw 'User id is required';
        }
        return this.#users_endpoint + "/" + user_id;
      case this.ENDPOINTS.USER_PWD_RESET_POST:
        return this.#users_endpoint + "/" + ENDPOINTS.RESET;
      case this.ENDPOINTS.USER_PATCH_PUSH_TOKEN:
        if(!user_id){
          throw 'User id is required';
        }
        return this.#users_endpoint + "/" + user_id + "/" + ENDPOINTS.NOTIFICATIONS + "/" + ENDPOINTS.TOKEN;

      case this.ENDPOINTS.GROUPS_GET:
        url = this.#groups_endpoint;
        return appendQueryParams(url, query_parameters);

      case this.ENDPOINTS.GROUP_POST:
        return this.#groups_endpoint;
      case this.ENDPOINTS.GROUP_DELETE:
      case this.ENDPOINTS.GROUP_GET:
      case this.ENDPOINTS.GROUP_PATCH:
        if(!group_id){
          throw 'Group id is required';
        }
        return this.#groups_endpoint + "/" + group_id;

      case this.ENDPOINTS.CUSTOMERS_GET:
        url = this.#customers_endpoint;
        return appendQueryParams(url, query_parameters);

      case this.ENDPOINTS.CUSTOMER_POST:
        return this.#customers_endpoint;
      case this.ENDPOINTS.CUSTOMER_DELETE:
      case this.ENDPOINTS.CUSTOMER_GET:
      case this.ENDPOINTS.CUSTOMER_PATCH:
        if(!customer_id){
          throw 'Customer id is required';
        }
        return this.#customers_endpoint + "/" + customer_id;

      case this.ENDPOINTS.ORDERS_GET:
        url = this.#orders_endpoint;
        return appendQueryParams(url, query_parameters);

      case this.ENDPOINTS.ORDER_POST:
        return this.#orders_endpoint;
      case this.ENDPOINTS.ORDER_DELETE:
      case this.ENDPOINTS.ORDER_GET:
      case this.ENDPOINTS.ORDER_PATCH:
        if(!order_id){
          throw 'Order id is required';
        }
        return this.#orders_endpoint + "/" + order_id;

      case this.ENDPOINTS.ORDER_STATS_GET:
        if(!order_id){
          throw 'Order id is required';
        }
        return this.#orders_endpoint + "/" + order_id + "/stats";

      case this.ENDPOINTS.DOM_GET_STATUSES:
        return this.#dom_endpoint + "/" + ENDPOINTS.STATUSES;
      case this.ENDPOINTS.DOM_GET_PROGRESSIONS:
        return this.#dom_endpoint + "/" + ENDPOINTS.PROGRESSIONS;
      case this.ENDPOINTS.DOM_GET_PROGRESSION_SUBSTATUSES:
        return this.#dom_endpoint + "/" + ENDPOINTS.PROGRESSIONS + "/" + ENDPOINTS.SUBSTATUSES;
      case this.ENDPOINTS.DOM_GET_PROVINCES:
        return this.#dom_endpoint + "/" + ENDPOINTS.PROVINCES;
      case this.ENDPOINTS.DOM_GET_NOTIFICATIONS:
        return this.#dom_endpoint + "/" + ENDPOINTS.NOTIFICATIONS + "/" + ENDPOINTS.EVENTS;

      case this.ENDPOINTS.LABELS_GET:
        if(!language_code){
          throw 'Missing language_code param';
        }
        if(!labels_prefix){
          throw 'Missing labels_prefix param';
        }
        return this.#labels_endpoint + "/" + language_code + "?" + QS_PARAMS.LABEL_PREFIX + labels_prefix;

      case this.ENDPOINTS.UPLOAD_MEDIA:
        return this.#media_endpoint + (media_id ? "/" + media_id : '');
      case this.ENDPOINTS.GET_MEDIA:
      case this.ENDPOINTS.DELETE_MEDIA:
        if(!media_id){
          throw 'Missing media_id param';
        }
        return this.#media_endpoint + "/" + media_id;

      case this.ENDPOINTS.UPLOAD_TYPES_GET:
        url = this.#media_endpoint + "/" + ENDPOINTS.CATEGORIES;
        return appendQueryParams(url, query_parameters);
      case this.ENDPOINTS.UPLOAD_TYPE_POST:
        return this.#media_endpoint + "/" + ENDPOINTS.CATEGORIES;
      case this.ENDPOINTS.UPLOAD_TYPE_GET:
      case this.ENDPOINTS.UPLOAD_TYPE_PATCH:
        if(!upload_type_id){
          throw 'Upload type id is required';
        }
        return this.#media_endpoint + "/" + ENDPOINTS.CATEGORIES + "/" + upload_type_id;

      case this.ENDPOINTS.ORDER_TYPES_GET:
        url = this.#orders_endpoint + "/" + ENDPOINTS.CATEGORIES;
        return appendQueryParams(url, query_parameters);
      case this.ENDPOINTS.ORDER_TYPE_POST:
        return this.#orders_endpoint + "/" + ENDPOINTS.CATEGORIES;
      case this.ENDPOINTS.ORDER_TYPE_GET:
      case this.ENDPOINTS.ORDER_TYPE_PATCH:
        if(!order_type_id){
          throw 'Order type id is required';
        }
        return this.#orders_endpoint + "/" + ENDPOINTS.CATEGORIES + "/" + order_type_id;

      case this.ENDPOINTS.GLOBALS_GET:
        return this.#config_endpoint + "/" + ENDPOINTS.GLOBALS;

      default:
        throw 'Endpoint not implemented';
    }
  }
}

function handleWorkerMessage({data: msg}){
  const observers = WorkerMessageObserver[msg.code];

  const observerError = result => {
    const statusObj = result ? result.status ? result : result.result : result;
    if(statusObj){
      for(let i = observers.length - 1; i >= 0; --i){
        const o = observers[i];
        if(o.onFail){
          if(o.onFail(result) !== false){
            observers.splice(i, 1);
          }
        }
      }
    }
  };

  const observerSuccess = result => {
    for(let i = observers.length - 1; i >= 0; --i){
      const o = observers[i];
      if(o.onSuccess){
        if(o.onSuccess(result) !== false){
          observers.splice(i, 1);
        }
        continue;
      }
      observers.splice(i, 1);
    }
  };

  const result = msg.response;
  switch (msg.status){
    case WorkerResponse.ERROR:{
      console.error(msg);
      const userStore = useUserStore();
      if(msg.http_code === 401 && userStore.isLoggedIn){
        const appStore = useAppStore();
        const {expiredLogout} = storeToRefs(appStore);
        if(expiredLogout.value){ //errore già notificato; inutile ri-notificarlo
          return;
        }
        expiredLogout.value = true;
        appStore.showError({body_key: 'global.session_expired'}); //qui non gli piace usare l'i18n e $t, quindi passo la string key
        userStore.logout(true).then();
        return;
      }
      observerError(msg);
      break;
    }
    case WorkerResponse.SUCCESS:{
      observerSuccess({result: result, contentType: msg.response_type});
      break;
    }
  }
}

function appendQueryParams(url, query_params){
  if(query_params){
    url += "?" + new URLSearchParams(query_params);
  }
  return url;
}
